<template>
    <v-row v-if="!headless" class="filters">
        <v-col
            :cols="filterWidth(filterconf)"
            :lg="filterLgWidth(filterconf)"
            :md="filterMdWidth(filterconf)"
            :sm="filterSmWidth(filterconf)"
            :key="JSON.stringify(filterconf)"
            v-show="displayFilter(filterconf) && shouldDisplayFilter(filterconf)"
            v-for="filterconf in fullFiltersConf"
        >
            <FiltersItem
                :filter="filterconf"
                :value="filters ? filters[filterconf.field] : null"
                :filters="filters"
                @change="filterChanged(filterconf, $event)"
            />
        </v-col>
    </v-row>
</template>

<script>
import Vue from "vue";
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";
import FiltersItem from "./filters-item";
import {
    groupCatalogChoices,
    normalizeFilterStoredValue,
} from "@/utils/filterChoices";
import { migrateCatalogEntryFormatFilter } from "@/utils/apiFilterParams";
import { applyTableFiltersLayout } from "@/utils/tableFiltersLayout";

export default {
    components: {
        FiltersItem,
    },
    mixins: [GlobalMixins],
    props: {
        filtersConf: {
            type: Array,
            required: false,
        },
        filters: {
            type: Object,
            required: false,
        },
        enableOrdering: {
            type: Boolean,
            default: false,
        },
        excludeFromOrdering: {
            type: Array,
            required: false,
        },
        displayFilters: {
            type: Boolean,
            required: false,
            default: true,
        },
        headless: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        presets() {
            return {
                date: {
                    type: "daterange",
                    field: "daterange",
                    label: this.$translate("date"),
                    icon: "date",
                },
                search: {
                    type: "search",
                    label: this.$translate("search"),
                    field: "search",
                    icon: "search",
                    force_display: true,
                },
                provider: {
                    type: "searchprovider",
                    source: "provider",
                    field: "provider",
                    label: this.$translate("provider"),
                    icon: "provider",
                    params: { ordering: "name", exclude_profile: "operator" },
                },
                providercatalog: {
                    type: "searchselect",
                    source: "catalog",
                    field: "catalog",
                    label: this.$translate("catalog"),
                    icon: "catalog",
                    params: {},
                },
                vendor: {
                    type: "searchselect",
                    source: "vendor",
                    field: "customer",
                    label: this.$translate("provider"),
                    endpoint: "/api/catalogs/",
                    icon: "provider",
                    params: { ordering: "name", owned: false },
                },
                billed: {
                    type: "searchselect",
                    source: "entity",
                    field: "billed",
                    label: this.$translate("customer"),
                    endpoint: "/api/entities/",
                    icon: "customer",
                    params: { ordering: "name" },
                },
                catalog: {
                    type: "searchselect",
                    source: "catalog",
                    field: "catalog",
                    label: this.$translate("catalog"),
                    endpoint: "/api/catalogs/",
                    icon: "catalog",
                    params: { ordering: "owner_name,name" },
                    transform: this.transformCatalogChoices,
                },
                format: {
                    type: "formatselect",
                    // DRF reserves the "format" query param for content negotiation.
                    field: "product_format",
                    label: this.$translate("productFormat"),
                    endpoint: "/api/formats/",
                    icon: "format",
                    text_field: "name",
                    value_field: "id",
                },
                catalogFormat: {
                    type: "formatselect",
                    // Use sale_format: DRF reserves the "format" query param for content negotiation.
                    field: "sale_format",
                    label: this.$translate("saleFormat"),
                    endpoint: "/api/formats/",
                    icon: "format",
                    text_field: "name",
                    value_field: "id",
                },
                // Legacy alias for catalog entry sale format filter.
                saleformat: {
                    type: "formatselect",
                    field: "sale_format",
                    label: this.$translate("saleFormat"),
                    endpoint: "/api/formats/",
                    icon: "format",
                    text_field: "name",
                    value_field: "id",
                },
                catalogentry_category: {
                    type: "combo",
                    source: "catalogentry_category",
                    field: "category",
                    label: this.$translate("category"),
                    endpoint: "/api/catalogcategories/",
                    icon: "category",
                },
                country: {
                    type: "country",
                    field: "country",
                    label: this.$translate("country"),
                    icon: "country",
                },
                storage_location: {
                    type: "select", source: "storage_location", field: "storage_location",
                    label: this.$translate("storageLocation"), endpoint: "/api/stocks/", icon: "stock",
                    params: { ordering: "name" },
                },
                stock: {
                    type: "select",
                    source: "stock",
                    field: "stock",
                    label: this.$translate("warehouse"),
                    endpoint: "/api/stocks/",
                    icon: "stock",
                    params: { ordering: "owner_name,name" },
                },
                /**
                 * Product list: whose catalog stock lines are shown (entity vs supplier SKUs).
                 * Endpoint returns { entity_name, providers[] }; values are entity | provider_<id>.
                 */
                stock_inventory_owner: {
                    type: "searchselect",
                    source: "stock_owner",
                    field: "stock_owner",
                    label: this.$translate("inventoryOwner"),
                    icon: "owner",
                    endpoint: "/api/products/stock-inventory-owner-choices/",
                    menu_props: { attach: true },
                },
                product: {
                    type: "searchselect",
                    source: "product",
                    field: "product",
                    label: this.$translate("product"),
                    endpoint: "/api/products/",
                    icon: "product",
                    params: { ordering: "name", limit: 20 },
                },
                /** Provider product stock tab: warehouse choices from loaded stock lines (not /api/stocks/). */
                stockproduct_list_warehouse: {
                    type: "select",
                    field: "stock",
                    label: this.$translate("warehouse"),
                    icon: "stock",
                    sort: true,
                    clearable: true,
                    choices: [],
                },
                /** Provider product stock tab: owner / supplier labels from loaded lines + active entity. */
                stockproduct_list_owner: {
                    type: "select",
                    field: "stock_owner",
                    label: this.$translate("stockproductListOwner"),
                    icon: "owner",
                    sort: true,
                    clearable: true,
                    choices: [],
                },
                customer: {
                    type: "searchcustomer",
                    source: "customer",
                    field: "customer",
                    label: this.$translate("customer"),
                    icon: "customer",
                    params: { ordering: "name" },
                },
                recipient: {
                    type: "searchcustomer",
                    source: "customer",
                    field: "recipient",
                    label: this.$translate("customer"),
                    icon: "customer",
                    params: { ordering: "name" },
                },
                receipt_owner: {
                    type: "searchcustomer",
                    source: "customer",
                    field: "customer",
                    label: this.$translate("customer"),
                    icon: "customer",
                    params: { ordering: "name" },
                },
                initiator: {
                    type: "searchcustomer",
                    source: "customer",
                    field: "initiator",
                    label: this.$translate("customer"),
                    icon: "customer",
                    params: { ordering: "name" },
                },
                deliverysite: {
                    type: "searchselect",
                    source: "deliverysite",
                    field: "deliverysite",
                    label: this.$translate("deliverySite"),
                    endpoint: "/api/deliverysites/",
                    icon: "site",
                    params: { field: "name" },
                    order: true,
                },
                establishment: {
                    type: "searchselect",
                    text_field: "name",
                    value_field: "id",
                    field: "establishment",
                    label: this.$translate("establishment"),
                    endpoint: "/api/establishments/",
                    icon: "establishment",
                    params: { ordering: "-main,name", archived: false, limit: 500 },
                },
                manufacturer: {
                    type: "searchselect",
                    source: "productmetadata",
                    field: "manufacturer",
                    label: this.$translate("manufacturer"),
                    endpoint: "/api/productsmetadata/",
                    icon: "manufacturer",
                    params: { field: "manufacturer" },
                    order: true,
                },
                movement_type: {
                    type: "select",
                    field: "movement_type",
                    icon: "movement",
                    label: this.$translate("movementType"),
                    sort: true,
                    choices: [
                        {
                            text: this.$translate("shipment"),
                            value: "shipment",
                        },
                        {
                            text: this.$translate("shipmentCanceled"),
                            value: "shipmentcanceled",
                        },
                        {
                            text: this.$translate("receipt"),
                            value: "receipt",
                        },
                        {
                            text: this.$translate("adjustment"),
                            value: "adjustment",
                        },
                    ],
                    order: true,
                },
                persona: {
                    type: "select",
                    field: "persona",
                    icon: "persona",
                    label: this.$translate("persona"),
                    sort: true,
                    choices: this.$utils.getPersonasChoices(),

                    order: true,
                },
                connected: {
                    type: "select",
                    field: "connected",
                    icon: "link",
                    label: this.$translate("connected"),
                    sort: false,
                    force_value: true,
                    choices: [
                        {
                            text: this.$translate("yes"),
                            value: true,
                        },
                        {
                            text: this.$translate("no"),
                            value: false,
                        },
                    ],
                },
                tags: {
                    type: "select",
                    source: "tags",
                    field: "tags",
                    label: this.$translate("tags"),
                    endpoint: "/api/tags/",
                    icon: "tag",
                    multiple: true,
                    params: {},
                },
                producttype: {
                    type: "select",
                    source: "productmetadata",
                    field: "producttype",
                    label: this.$translate("productType"),
                    endpoint: "/api/productsmetadata/",
                    params: { field: "producttype" },
                    icon: "producttype",
                    order: false,
                },
                productmetadatafilters: {
                    type: "dynamicfilters",
                    field: "producttype",
                    filters: this.getProductMetadataFilters,
                },
                catalogentry_recurring: {
                    type: "select",
                    icon: "status",
                    label: this.$translate("recurrence"),
                    field: "recurring",
                    sort: true,
                    choices: [
                        { text: this.$translate("recurring"), value: true },
                        { text: this.$translate("oneshot"), value: false },
                    ],
                    order: true,
                },
                catalogentry_type: {
                    type: "combo",
                    icon: "status",
                    label: this.$translate("type"),
                    field: "type",
                    sort: true,
                    choices: [
                        { text: this.$translate("product"), value: "product" },
                        { text: this.$translate("service"), value: "service" },
                    ],
                    order: true,
                },
                catalogentry_trigger: {
                    type: "select",
                    icon: "status",
                    label: this.$translate("trigger"),
                    field: "trigger",
                    sort: true,
                    choices: [
                        { text: this.$translate("manual"), value: "manual" },
                        {
                            text: this.$translate("shipmentPrepared"),
                            value: "shipment-prepared",
                        },
                        {
                            text: this.$translate("shipmentDelivered"),
                            value: "shipment-delivered",
                        },
                        {
                            text: this.$translate("shipmentPickedUp"),
                            value: "shipment-pickedup",
                        },
                        {
                            text: this.$translate("shipmentOption"),
                            value: "shipment-option",
                        },
                        {
                            text: this.$translate("receiptHandled"),
                            value: "receipt-handled",
                        },
                        {
                            text: this.$translate("receiptOption"),
                            value: "receipt-option",
                        },
                        { text: this.$translate("stock"), value: "stock" },
                    ],
                    order: true,
                },
                city: {
                    field: "city",
                    label: this.$translate("city"),
                    order: true,
                },
                postal_code: {
                    field: "postal_code",
                    label: this.$translate("postalCode"),
                    order: true,
                },
                customerorder_status: {
                    field: "status",
                    type: "select",
                    icon: "status",
                    multiple: true,
                    label: this.$translate("customerOrderStatus"),
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "customerorder.status.draft"
                            ),
                            value: "draft",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerorder.status.to_be_processed"
                            ),
                            value: "to_be_processed",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerorder.status.processing"
                            ),
                            value: "processing",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerorder.status.completed"
                            ),
                            value: "completed",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerorder.status.canceled"
                            ),
                            value: "canceled",
                        },
                    ],
                    order: true,
                },
                providerorder_status: {
                    field: "status",
                    type: "select",
                    icon: "status",
                    multiple: true,
                    label: this.$translate("status"),
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "providerorder.status.draft"
                            ),
                            value: "draft",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerorder.status.to_be_processed"
                            ),
                            value: "to_be_processed",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerorder.status.waiting"
                            ),
                            value: "waiting",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerorder.status.processing"
                            ),
                            value: "processing",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerorder.status.completed"
                            ),
                            value: "completed",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerorder.status.canceled"
                            ),
                            value: "canceled",
                        },
                    ],
                    order: true,
                },
                commission_status: {
                    field: "agent_commission_status",
                    type: "select",
                    icon: "status",
                    multiple: true,
                    label: this.$translate("commissionStatus"),
                    choices: [
                    {
                            text: Vue.prototype.$translate(
                                "agentcommission.status.waiting"
                            ),
                            value: "waiting",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "agentcommission.status.ready"
                            ),
                            value: "ready",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "agentcommission.status.billed"
                            ),
                            value: "billed",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "agentcommission.status.paid"
                            ),
                            value: "paid",
                        },
                    ],
                    order: true,
                },
                allocation_status: {
                    field: "status",
                    type: "select",
                    icon: "status",
                    label: this.$translate("status"),
                    choices: [
                        { text: this.$translate("pending"), value: "pending" },
                        {
                            text: this.$translate("opened"),
                            value: "opened",
                        },
                        {
                            text: this.$translate("exhausted"),
                            value: "exhausted",
                        },
                        {
                            text: this.$translate("closed"),
                            value: "closed",
                        },
                    ],
                    order: true,
                },
                shipment_status: {
                    field: "contextual_status",
                    type: "select",
                    icon: "status",
                    label: this.$translate("status"),
                    multiple: true,
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.draft"
                            ),
                            value: "draft",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.to_be_processed"
                            ),
                            value: "to_be_processed",
                        },
                        {
                            text: Vue.prototype.$translate("shipment.status.waiting"),
                            value: "waiting",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.preparing"
                            ),
                            value: "preparing",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.prepared"
                            ),
                            value: "prepared",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.shipping"
                            ),
                            value: "shipping",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.picked_up"
                            ),
                            value: "picked_up",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.delivered"
                            ),
                            value: "delivered",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.complete"
                            ),
                            value: "complete",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.canceled"
                            ),
                            value: "canceled",
                        },
                    ],
                    order: true,
                },
                delegatedshipment_status: {
                    field: "status",
                    type: "select",
                    icon: "status",
                    label: this.$translate("status"),
                    multiple: true,
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.waiting"
                            ),
                            value: "waiting",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.preparing"
                            ),
                            value: "preparing",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.prepared"
                            ),
                            value: "prepared",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "shipment.status.shipping"
                            ),
                            value: "shipping",
                        },
                    ],
                    order: true,
                },
                receipt_placed: {
                    field: "placed",
                    type: "select",
                    icon: "placed",
                    label: this.$translate("placed"),
                    choices: [
                        {
                            text: this.$translate("any"),
                            value: null,
                        },
                        {
                            text: this.$translate("yes"),
                            value: true,
                        },
                        {
                            text: this.$translate("no"),
                            value: false,
                        },
                    ],
                    order: false,
                },
                receipt_status: {
                    field: "contextual_status",
                    type: "select",
                    icon: "status",
                    multiple: true,
                    label: this.$translate("status"),
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.draft"
                            ),
                            value: "draft",
                        },

                        {
                            text: Vue.prototype.$translate("receipt.status.to_be_processed"),
                            value: "to_be_processed",
                        },
                        {
                            text: Vue.prototype.$translate("receipt.status.waiting"),
                            value: "waiting",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.handling"
                            ),
                            value: "handling",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.complete"
                            ),
                            value: "complete",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.partial"
                            ),
                            value: "partial",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.missing"
                            ),
                            value: "missing",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.canceled"
                            ),
                            value: "canceled",
                        },
                    ],
                    order: true,
                },
                receipt_kind: {
                    field: "kind",
                    type: "select",
                    icon: "receipt",
                    label: this.$translate("receipt.kindLabel"),
                    choices: [
                        {
                            text: Vue.prototype.$translate("receipt.kind.standard"),
                            value: "standard",
                        },
                        {
                            text: Vue.prototype.$translate("receipt.kind.return"),
                            value: "return",
                        },
                    ],
                    order: true,
                },
                delegatedreceipt_status: {
                    field: "status",
                    type: "select",
                    icon: "status",
                    multiple: true,
                    label: this.$translate("status"),
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.waiting"
                            ),
                            value: "waiting",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "receipt.status.handling"
                            ),
                            value: "handling",
                        },
                    ],
                    order: true,
                },
                accounting_period: {
                    type: "select",
                    field: "accounting_period",
                    icon: "mdi-calendar-range",
                    label: this.$translate("accountingPeriod"),
                    endpoint: "/api/accounting-periods/",
                    text_field: "label",
                    value_field: "id",
                },
                invoice_type: {
                    type: "select",
                    field: "invoice_type",
                    icon: "invoice",
                    label: this.$translate("invoiceType"),
                    choices: [
                        {
                            text: this.$translate("oneshot"),
                            value: "oneshot",
                        },
                        { text: this.$translate("period"), value: "period" },
                    ],
                    order: true,
                },

                customerinvoice_status: {
                    type: "select",
                    field: "invoice_status",
                    icon: "status",
                    multiple: true,
                    label: Vue.prototype.$translate("status_raw"),
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "customerinvoice.status.draft"
                            ),
                            value: "draft",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerinvoice.status.tosend"
                            ),
                            value: "tosend",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerinvoice.status.waitingpayment"
                            ),
                            value: "waitingpayment",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerinvoice.status.overdue"
                            ),
                            value: "overdue",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerinvoice.status.paid"
                            ),
                            value: "paid",
                        },
                    ],
                    order: true,
                },
                providerinvoice_status: {
                    type: "select",
                    field: "invoice_status",
                    icon: "status",
                    multiple: true,
                    label: this.$translate("providerInvoiceStatus"),
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "providerinvoice.status.draft"
                            ),
                            value: "draft",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerinvoice.status.missing"
                            ),
                            value: "missing",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerinvoice.status.tovalidate"
                            ),
                            value: "tovalidate",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerinvoice.status.waiting"
                            ),
                            value: "waitingpayment",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerinvoice.status.overdue"
                            ),
                            value: "overdue",
                        },
                        {
                            text: Vue.prototype.$translate(
                                "providerinvoice.status.paid"
                            ),
                            value: "paid",
                        },
                    ],
                    order: true,
                },
                invoice_paid: {
                    type: "select",
                    field: "paid",
                    icon: "euro",
                    label: this.$translate("paymentStatus"),
                    choices: [
                        {
                            text: this.$translate("invoicePaid"),
                            value: true,
                        },
                        {
                            text: this.$translate("waitingPayment"),
                            value: false,
                        },
                    ],
                    order: true,
                },
                invoice_credit: {
                    type: "select",
                    field: "credit",
                    icon: "euro",
                    label: this.$translate("creditDebit"),
                    choices: [
                        {
                            text: this.$translate("debit"),
                            value: false,
                        },
                        {
                            text: this.$translate("credit"),
                            value: true,
                        },
                    ],
                    order: true,
                },
                payment_method: {
                    type: "select",
                    field: "payment_method",
                    icon: "euro",
                    multiple: true,
                    label: this.$translate("paymentMethod"),
                    choices: [
                        { text: this.$translate("paymentMethodUnset"), value: "none" },
                        { text: this.$translate("paymentMethodTransfer"), value: "transfer" },
                        { text: this.$translate("paymentMethodDirectDebit"), value: "direct_debit" },
                        { text: this.$translate("paymentMethodBillOfExchange"), value: "bill_of_exchange" },
                        { text: this.$translate("paymentMethodCheck"), value: "check" },
                        { text: this.$translate("paymentMethodCard"), value: "card" },
                        { text: this.$translate("paymentMethodCash"), value: "cash" },
                    ],
                    order: true,
                },
                invoice_date: {
                    field: "invoice_date",
                    type: "daterange",
                    label: this.$translate("invoiceDate"),
                    icon: "date",
                },
                due_date: {
                    field: "due_date",
                    type: "daterange",
                    label: this.$translate("dueDate"),
                    icon: "date",
                },
                invoice_provider: {
                    type: "searchselect",
                    field: "provider",
                    icon: "provider",
                    label: this.$translate("provider"),
                    source: "provider",
                    endpoint: "/api/providers/",
                    params: { ordering: "name" },
                },
                billingaccount: {
                    type: "select",
                    field: "billingaccount",
                    icon: "invoice",
                    label: this.$translate("billingAccount"),
                    order: true,
                    source: "billingaccount",
                    endpoint: "/api/billingaccounts/",
                    params: { ordering: "name" },
                },
                exclude_readonly_shipments: {
                    field: "exclude_readonly",
                    type: "boolean",
                    label: this.$translate("excludeCompleted"),
                },
                delegated: {
                    field: "delegated",
                    type: "boolean",
                    label: this.$translate("delegated"),
                },
                paid: {
                    field: "paid",
                    type: "boolean",
                    label: this.$translate("invoicePaid"),
                },
                payment_status: {
                    field: "paid",
                    type: "select",
                    icon: "euro",
                    label: this.$translate("paymentStatus"),
                    choices: [
                        {
                            text: Vue.prototype.$translate(
                                "customerinvoice.status.paid"
                            ),
                            value: true,
                        },
                        {
                            text: Vue.prototype.$translate(
                                "customerinvoice.status.waitingpayment"
                            ),
                            value: false,
                        },
                    ],
                    order: true,
                },
                completed_date: {
                    field: "completed_date",
                    type: "daterange",
                    label: this.$translate("completionDate"),
                    icon: "date",
                },
                validated_date: {
                    field: "validated_date",
                    type: "daterange",
                    label: this.$translate("validatedDate"),
                    icon: "date",
                },
                paid_date: {
                    field: "paid_date",
                    type: "daterange",
                    label: this.$translate("paidDate"),
                    icon: "date",
                },
                exclude_readonly_orders: {
                    field: "exclude_readonly",
                    type: "boolean",
                    label: this.$translate("excludeCompleted"),
                },
                include_archived: {
                    field: "include_archived",
                    type: "boolean",
                    label: this.$translate("includeArchived"),
                },
                include_canceled: {
                    field: "include_canceled",
                    type: "boolean",
                    label: this.$translate("includeCanceled"),
                },
                include_archived_products: {
                    field: "include_archived_products",
                    type: "boolean",
                    label: this.$translate("includeArchivedProducts"),
                },
                exclude_entries_with_no_stock: {
                    field: "with_stock",
                    type: "boolean",
                    label: this.$translate("excludeEmptyStock"),
                },
                exclude_entries_with_no_provider_stock: {
                    field: "with_provider_stock",
                    type: "boolean",
                    label: this.$translate("excludeEmptyProviderStock"),
                },
                exclude_products_with_no_stock: {
                    field: "with_stock",
                    type: "boolean",
                    label: this.$translate("excludeEmptyStock"),
                },
                missing_purchase_price: {
                    field: "missing_purchase_price",
                    type: "boolean",
                    label: this.$translate("missingPurchasePrice"),
                },
                exclude_readonly_receipts: {
                    field: "exclude_readonly",
                    type: "boolean",
                    label: this.$translate("excludeCompleted"),
                },
                delivery_date: {
                    field: "delivery_date",
                    type: "daterange",
                    label: this.$translate("deliveryDate"),
                    icon: "date",
                },
                assigned_to: {
                    field: "assigned_to",
                    type: "select",
                    source: "user",
                    label: this.$translate("assignedTo"),
                    icon: "take",
                    endpoint: "/api/users/",
                    params: {
                        ordering: "full_name",
                        myentity: true,
                        impersonification: false,
                        is_active: true,
                    },
                },
                receipt_date: {
                    field: "receipt_date",
                    type: "daterange",
                    label: this.$translate("receiptDate"),
                    icon: "date",
                },
                shipment_date: {
                    field: "shipment_date",
                    type: "daterange",
                    label: this.$translate("shipmentDate"),
                    icon: "date",
                },
                shop_providers: {
                    type: "select",
                    source: "provider",
                    field: "providers",
                    label: this.$translate("shop.selectProviders"),
                    icon: "providers",
                    endpoint: "/api/providers/",
                    params: { ordering: "name", limit: 10000 },
                    multiple: true,
                },
                shop_producttypes: {
                    type: "select",
                    source: "producttype",
                    field: "producttypes",
                    label: this.$translate("shop.selectProductTypes"),
                    icon: "producttype",
                    endpoint: "/api/producttypes/",
                    params: { ordering: "label", limit: 10000 },
                    multiple: true,
                    text_field: "label",
                    value_field: "id",
                },
                low_stock: {
                    field: "low_stock",
                    type: "boolean",
                    label: this.$translate("shop.lowStock"),
                },
            };
        },
    },
    data() {
        return {
            timeout_counter: 0,
            fullFiltersConf: [],
        };
    },
    async mounted() {
        await this.refreshFiltersConf();
    },
    watch: {
        filtersConf: {
            handler() {
                this.refreshFiltersConf();
            },
        },
        fullFiltersConf: {
            handler() {
                this.emitConfUpdated();
            },
            deep: true,
        },
    },
    methods: {
        async refreshFiltersConf() {
            this.fullFiltersConf = await this.getFullFiltersConf(this.filters);
            this.emitConfUpdated();
        },
        emitConfUpdated() {
            this.$emit("conf-updated", this.fullFiltersConf);
        },
        resetFilters(extraClearKeys = []) {
            let newFilters = {...this.filters};
            // Iterate over fullFiltersConf and remove their fields from newFilters. This leaves only non-filter fields (like pagination, ordering, etc).
            this.fullFiltersConf.forEach((filterconf) => {
                if (filterconf && filterconf.field) {
                    delete newFilters[filterconf.field];
                    if (
                        filterconf.type === "daterange" ||
                        filterconf.type === "numberrange"
                    ) {
                        delete newFilters[`${filterconf.field}_min`];
                        delete newFilters[`${filterconf.field}_max`];
                        if (filterconf.type === "daterange") {
                            delete newFilters[`${filterconf.field}_preset`];
                        }
                    }
                }
            });
            if (newFilters.ordering) {
                delete newFilters.ordering;
            }
            (extraClearKeys || []).forEach((key) => {
                delete newFilters[key];
            });
            this.$emit("on-filters-change", newFilters);
        },
        transformCatalogChoices(choices) {
            return groupCatalogChoices(choices, {
                myCatalogsLabel: this.$translate("filters.myCatalogs"),
                agencyCatalogsLabel: this.$translate("filters.agencyCatalogs"),
            });
        },
        filterHasValue(filterconf) {
            if (!this.filters) {
                return false;
            }
            
            // For range filters, check both min and max fields
            if (
                filterconf.type === "daterange" ||
                filterconf.type === "numberrange"
            ) {
                if (
                    filterconf.type === "daterange" &&
                    this.filters[`${filterconf.field}_preset`]
                ) {
                    return true;
                }
                const minField = `${filterconf.field}_min`;
                const maxField = `${filterconf.field}_max`;
                return !!(this.filters[minField] || this.filters[maxField]);
            }
            
            // For other filters, check if the field has a value
            const value = this.filters[filterconf.field];
            
            // Check for empty/null/undefined values
            if (value === null || value === undefined || value === "") {
                return false;
            }

            if (filterconf.type === "slider") {
                const n = Number(value);
                return Number.isFinite(n) && n > 0;
            }
            
            // For arrays, check if not empty
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            
            return true;
        },
        displayFilter(filterconf) {
            // Always display if forced, if filters are shown, or if the filter has an active value
            return filterconf.force_display || this.displayFilters || this.filterHasValue(filterconf);
        },
        shouldDisplayFilter(filterconf) {
            if (typeof filterconf.display === "function") {
                return filterconf.display(this.filters);
            }
            return true;
        },
        getOrderingFilter(fullfiltersconf) {
            fullfiltersconf = fullfiltersconf.filter(
                (filter) =>
                    filter.order &&
                    (this.excludeFromOrdering || []).indexOf(filter.field) == -1
            );
            let orderingchoices = fullfiltersconf
                .map((filter) => {
                    return {
                        text: filter.label,
                        value: filter.field,
                    };
                })
                .sort((item1, item2) =>
                    item1.text < item2.text
                        ? -1
                        : item2.text < item2.text
                        ? 1
                        : 0
                );
            if (!orderingchoices.length) {
                return null;
            }
            return {
                field: "ordering",
                type: "select",
                icon: "placed",
                label: this.$translate("orderBy"),
                choices: orderingchoices,
            };
        },
        async getFullFiltersConf(filters) {
            if (!this.filtersConf) {
                return [];
            }
            let fullfiltersconf = [];

            for (let filterConf of this.filtersConf) {
                let filterconf = this.filterConf(filterConf);
                if (filterconf.type == "dynamicfilters") {
                    let dynamicfilters = await filterconf.filters(
                        filterConf,
                        filters
                    );
                    dynamicfilters.forEach((dynamicfilterConf) => {
                        fullfiltersconf.push(dynamicfilterConf);
                    });
                } else {
                    fullfiltersconf.push(filterconf);
                }
            }
            return applyTableFiltersLayout(fullfiltersconf);
        },
        async getProductMetadataFilters(filterConf, filters) {
            if (!filters) {
                filters = this.filters;
            }

            let masterfield = filterConf.field || "producttype";
            if (!filters[masterfield]) {
                return [];
            }
            let url = `/api/producttypes/${filters[masterfield]}/`;
            const response = await axios.get(url);
            let producttype = response.data;
            if (!producttype) {
                return [];
            }
            return (producttype.fields || [])
                .filter((field) => field.filter)
                .map((field) => {
                    if (field.type == "choices") {
                        return {
                            ...(filterConf.params || {}),
                            type: "select",
                            label: field.label,
                            multiple: field.multiple,
                            unit: field.unit,
                            field: "metadata__" + field.code,
                            choices: field.choices.map((item) => {
                                return {
                                    text: item,
                                    value: item,
                                };
                            }),
                            order: true,
                        };
                    }
                    return {
                        type: field.type,
                        label: field.label,
                        unit: field.unit,
                        multiple: field.multiple,
                        source: "productmetadata",
                        endpoint:
                            filterConf.endpoint || "/api/productsmetadata/",
                        params: {
                            ...(filterConf.params || {}),
                            field: field.code,
                            producttype: producttype.id,
                        },
                        field: "metadata__" + field.code,
                        order: true,
                    };
                });
        },
        filterWidth(filterconf) {
            if (filterconf.type == "search") {
                return filterconf.cols || 12;
            }
            return filterconf.cols || 12;
        },
        filterSmWidth(filterconf) {
            if (filterconf.type == "search") {
                return filterconf.cols || 12;
            }
            return filterconf.cols || 6;
        },
        filterMdWidth(filterconf) {
            if (filterconf.type == "search") {
                return filterconf.cols || 6;
            }
            return filterconf.cols || 3;
        },
        filterLgWidth(filterconf) {
            if (filterconf.type == "search") {
                return filterconf.cols || 6;
            }
            return filterconf.cols || 3;
        },
        filterXsWidth(filterconf) {
            if (filterconf.type == "search") {
                return filterconf.cols || 12;
            }
            return filterconf.cols || 12;
        },
        filterConf(filter) {
            let filterconf = {};
            if (typeof filter === "object") {
                let preset = {};
                if (filter.preset) {
                    preset = { ...this.presets[filter.preset] };
                }
                filterconf = Object.assign(preset, filter);
            }

            if (typeof filter == "string") {
                if (this.presets[filter]) {
                    filterconf = Object.assign({}, this.presets[filter]);
                } else {
                    filterconf = {
                        field: filter,
                        type: "string",
                    };
                }
            } else if (typeof filter === "object") {
                if (filter.preset) {
                    filterconf = Object.assign(
                        {},
                        this.presets[filter.preset],
                        filter
                    );
                    // For tags preset, object_type is required (top-level or in params)
                    if (filter.preset === 'tags') {
                        const objectType =
                            filter.object_type || filter.params?.object_type;
                        if (!objectType) {
                            console.error(
                                'Tags filter requires object_type to be specified. Filter:',
                                filter
                            );
                            throw new Error(
                                'Tags filter requires object_type to be specified'
                            );
                        }
                        filterconf.params = {
                            ...(filterconf.params || {}),
                            object_type: objectType,
                        };
                        filterconf.object_type = objectType;
                    }
                }
            }
            filterconf.field =
                filterconf.field || filterconf.source || filterconf.preset;
            // Daterange filters expose preset pills (this week, this month, ...)
            // everywhere by default, unless explicitly disabled with
            // `dateRangePresets: false`.
            if (
                filterconf.type === "daterange" &&
                filterconf.dateRangePresets === undefined
            ) {
                filterconf.dateRangePresets = true;
            }
            return filterconf;
        },
        usesCatalogEntryFormatFilter(filtersConf) {
            return (filtersConf || []).some(
                (item) =>
                    item?.field === "sale_format" ||
                    item?.preset === "catalogFormat" ||
                    item?.preset === "saleformat",
            );
        },
        async filterChanged(filter, value) {
            let timeout_counter = ++this.timeout_counter;
            await new Promise((resolve) => setTimeout(resolve, 300));
            if (this.timeout_counter != timeout_counter) {
                return;
            }

            let filters = Object.assign({}, this.filters);

            if (
                (filter.type === "daterange" || filter.type === "numberrange") &&
                filter.field
            ) {
                const minField = `${filter.field}_min`;
                const maxField = `${filter.field}_max`;
                const presetField = `${filter.field}_preset`;
                const isPreset =
                    filter.type === "daterange" &&
                    value &&
                    value.preset !== undefined &&
                    value.preset !== null;

                if (isPreset) {
                    filters[presetField] = value.preset;
                    if (minField in filters) {
                        delete filters[minField];
                    }
                    if (maxField in filters) {
                        delete filters[maxField];
                    }
                } else if (value === null || (!value && !filter.force_value)) {
                    if (presetField in filters) {
                        delete filters[presetField];
                    }
                    if (minField in filters) {
                        delete filters[minField];
                    }
                    if (maxField in filters) {
                        delete filters[maxField];
                    }
                } else {
                    if (presetField in filters) {
                        delete filters[presetField];
                    }
                    const min =
                        value?.min === null || value?.min === undefined || value?.min === ""
                            ? null
                            : value.min;
                    const max =
                        value?.max === null || value?.max === undefined || value?.max === ""
                            ? null
                            : value.max;
                    if (min === null) {
                        delete filters[minField];
                    } else {
                        filters[minField] = min;
                    }
                    if (max === null) {
                        delete filters[maxField];
                    } else {
                        filters[maxField] = max;
                    }
                }
            } else {
                const noValue = value === null || value === undefined || value === "";
                const removeField = noValue || (value !== false && !value && !filter.force_value);
                if (removeField) {
                    if (filter.type === "select" && !filter.multiple) {
                        filters[filter.field] = null;
                    } else if (filter.field in filters) {
                        delete filters[filter.field];
                    }
                } else {
                    if (filter.type == "boolean" && filter.value) {
                        value = filter.value || value;
                    }
                    filters[filter.field] = normalizeFilterStoredValue(value, filter);
                }
            }
            if (filters.offset) {
                delete filters.offset;
            }
            let fullFiltersConf = await this.getFullFiltersConf(filters);
            this.fullFiltersConf = fullFiltersConf;

            if (this.usesCatalogEntryFormatFilter(fullFiltersConf)) {
                filters = migrateCatalogEntryFormatFilter(filters);
            }

            for (filter in filters) {
                if (filter == "offset" || filter == "limit") {
                    continue;
                }
                let filterConf = this.fullFiltersConf.find(
                    (item) => item.field == filter
                );
                if (!filterConf) {
                    continue;
                }
            }
            this.$emit("on-filters-change", filters);
        },
    },
    i18n: {
        messages: {
            en: {
                tags: "Tags",
                filters: {
                    myCatalogs: "My catalogs",
                    agencyCatalogs: "Agency catalogs",
                },
                search: "Search",
                inventoryOwner: "Owner",
                provider: "Provider",
                catalog: "Catalog",
                customer: "Customer",
                category: "Category",
                warehouse: "Warehouse",
                stockproductListOwner: "Owner / Supplier",
                deliverySite: "Delivery site",
                establishment: "Establishment",
                manufacturer: "Manufacturer",
                productFormat: "Product format",
                saleFormat: "Sale format",
                movementType: "Movement type",
                shipment: "Shipment",
                shipmentCanceled: "Shipment canceled",
                receipt: "Receipt",
                adjustment: "Adjustment",
                persona: "Persona",
                connected: "Connected",
                yes: "Yes",
                no: "No",
                productType: "Product type",
                recurrence: "Recurrence",
                recurring: "Recurring",
                oneshot: "Oneshot",
                type: "Type",
                product: "Product",
                service: "Service",
                trigger: "Trigger",
                manual: "Manual",
                shipmentPrepared: "Shipment prepared",
                shipmentDelivered: "Shipment delivered",
                shipmentPickedUp: "Shipment picked up",
                shipmentOption: "Shipment option",
                receiptHandled: "Receipt handled",
                receiptOption: "Receipt option",
                storageLocation: "Storage location",
                stock: "Stock",
                city: "City",
                postalCode: "Postal code",
                country: "Country",
                status: "Status",
                pending: "Pending",
                opened: "Opened",
                exhausted: "Exhausted",
                closed: "Closed",
                placed: "Passed",
                any: "Any",
                invoiceType: "Invoice type",
                period: "Period",
                paymentStatus: "Payment status",
                creditDebit: "Credit / Debit",
                debit: "Debit",
                credit: "Credit",
                billingAccount: "Billing account",
                excludeCompleted: "Exclude completed",
                delegated: "Delegated",
                invoicePaid: "Invoice paid",
                paymentMethod: "Payment method",
                paymentMethodUnset: "Not set",
                paymentMethodTransfer: "Wire transfer",
                paymentMethodDirectDebit: "Direct debit",
                paymentMethodBillOfExchange: "Accepted bill of exchange",
                paymentMethodCheck: "Check",
                paymentMethodCard: "Card terminal",
                paymentMethodCash: "Cash register",
                waitingPayment: "Waiting payment",
                completionDate: "Completion date",
                validatedDate: "Order date",
                paidDate: "Paid date",
                includeArchived: "Include archived",
                includeArchivedProducts: "Include archived products",
                includeCanceled: "Include canceled orders",
                customerOrderStatus: "Order status",
                excludeEmptyStock: "Exclude empty stock",
                excludeEmptyProviderStock: "Exclude empty provider stock",
                missingPurchasePrice: "Missing purchase price",
                deliveryDate: "Delivery date",
                receiptDate: "Receipt date",
                shipmentDate: "Shipment date",
                assignedTo: "Assigned to",
                orderBy: "Order by",
                providerInvoiceStatus: "Status",
                commissionStatus: "Commission status",
                invoiceDate: "Invoice date",
                accountingPeriod: "Accounting period",
                dueDate: "Due date",
                date: "Date",
                shop: {
                    selectProviders: "Select providers",
                    selectProductTypes: "Select product types",
                    lowStock: "Low stock only",
                },
            },
            fr: {
                tags: "Tags",
                filters: {
                    myCatalogs: "Mes catalogues",
                    agencyCatalogs: "Catalogues en agence",
                },
                search: "Rechercher",
                inventoryOwner: "Propriétaire",
                provider: "Fournisseur",
                catalog: "Catalogue",
                customer: "Client",
                category: "Catégorie",
                warehouse: "Entrepôt",
                stockproductListOwner: "Propriétaire / Fournisseur",
                deliverySite: "Adresse de livraison",
                establishment: "Établissement",
                manufacturer: "Fabricant",
                productFormat: "Format produit",
                saleFormat: "Format de vente",
                movementType: "Type de mouvement",
                shipment: "Expédition",
                shipmentCanceled: "Expédition annulée",
                receipt: "Réception",
                adjustment: "Ajustement",
                persona: "Persona",
                connected: "Connecté",
                yes: "Oui",
                no: "Non",
                productType: "Type de produit",
                recurrence: "Récurrence",
                recurring: "Récurrent",
                oneshot: "Ponctuel",
                type: "Type",
                product: "Produit",
                service: "Service",
                trigger: "Déclencheur",
                manual: "Manuel",
                shipmentPrepared: "Expédition préparée",
                shipmentDelivered: "Expédition livrée",
                shipmentPickedUp: "Expédition récupérée",
                shipmentOption: "Option d'expédition",
                receiptHandled: "Réception traitée",
                receiptOption: "Option de réception",
                storageLocation: "Lieu de stockage",
                stock: "Stock",
                city: "Ville",
                postalCode: "Code postal",
                country: "Pays",
                status: "Statut",
                pending: "En attente",
                opened: "Ouvert",
                exhausted: "Épuisé",
                closed: "Fermé",
                placed: "Passé",
                any: "Tous",
                invoiceType: "Type de facture",
                period: "Période",
                paymentStatus: "Statut de paiement",
                creditDebit: "Facture / Avoir",
                debit: "Facture",
                credit: "Avoir",
                billingAccount: "Compte de facturation",
                excludeCompleted: "Exclure les finalisées",
                delegated: "Délégué",
                invoicePaid: "Facture payée",
                paymentMethod: "Mode de règlement",
                paymentMethodUnset: "Non renseigné",
                paymentMethodTransfer: "Virement",
                paymentMethodDirectDebit: "Prélèvement",
                paymentMethodBillOfExchange: "Effet accepté",
                paymentMethodCheck: "Chèque",
                paymentMethodCard: "TPE",
                paymentMethodCash: "Caisse",
                waitingPayment: "En attente de paiement",
                completionDate: "Date de finalisation",
                validatedDate: "Date de commande",
                paidDate: "Date de paiement",
                includeArchived: "Inclure les archivés",
                includeArchivedProducts: "Inclure les produits archivés",
                includeCanceled: "Inclure les commandes annulées",
                customerOrderStatus: "Statut de la commande",
                excludeEmptyStock: "Exclure les stocks vides",
                excludeEmptyProviderStock:
                    "Exclure les stocks fournisseur vides",
                missingPurchasePrice: "Prix d'achat manquant",
                deliveryDate: "Date de livraison",
                receiptDate: "Date de réception",
                shipmentDate: "Date d'expédition",
                assignedTo: "Attribué à",
                orderBy: "Trier par",
                providerInvoiceStatus: "Statut",
                commissionStatus: "Statut de la commission",
                invoiceDate: "Date de facturation",
                accountingPeriod: "Période comptable",
                dueDate: "Date d'échéance",
                date: "Date",
                shop: {
                    selectProviders: "Sélectionner les fournisseurs",
                    selectProductTypes: "Sélectionner les types de produits",
                    lowStock: "Stock bas uniquement",
                },
            },
        },
    },
};
</script>

<style lang="scss">
.filters .filter label {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}
</style>
